import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDialog } from '../ui/useDialog';
import { X, Forward, Send, Search, Info, Loader2 } from 'lucide-react';
import { consolidatedBusinessApi } from '../../services/consolidatedBusinessApi';
import type { ForwardComposeData, ForwardEmployee } from '../../types/consBusinessMail';
import { useToast } from '../ui/Toast';
import { CB_STATUS_META, toCbStatus } from './status';
import { fmtDate } from './format';

interface Props {
    customerId: string;
    /** The combination the drawer shows — pre-ticked. */
    groupKey: string;
    onClose: () => void;
    onSent: () => void;
}

const SUBJECT_MAX = 300;
const MESSAGE_MAX = 10_000;
const MAX_GROUPS = 50;
const MAX_RECIPIENTS = 25;

type Target = 'to' | 'cc';

function defaultSubject(customerName: string, productNames: string[]): string {
    const first = productNames[0] ?? '';
    const more = productNames.length > 1 ? ` +${productNames.length - 1}` : '';
    return `Consolidated Business: ${customerName}${first ? ` — ${first}${more}` : ''}`.slice(0, SUBJECT_MAX);
}

/**
 * Forward a customer's combinations to colleagues. INTERNAL ONLY.
 *
 * It replaced the customer outreach modal on 2026-10-07. Recipients are picked from the
 * active-employee list; there is no free address field, and the server rejects any address
 * that is not an employee whatever this component sends. The mail body (customer, product
 * table, footer) is rendered by the server around the plain-text message typed here.
 */
export function ConsBusinessForwardModal({ customerId, groupKey, onClose, onSent }: Props) {
    const { toast } = useToast();
    const [data, setData] = useState<ForwardComposeData | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [selected, setSelected] = useState<string[]>([]);
    const [to, setTo] = useState<ForwardEmployee[]>([]);
    const [cc, setCc] = useState<ForwardEmployee[]>([]);
    const [search, setSearch] = useState('');
    const [target, setTarget] = useState<Target>('to');
    const [subject, setSubject] = useState('');
    const [subjectDirty, setSubjectDirty] = useState(false);
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        let cancelled = false;
        consolidatedBusinessApi.composeForward(customerId, groupKey)
            .then(d => {
                if (cancelled) return;
                setData(d);
                setSelected(d.selectedGroupKey ? [d.selectedGroupKey] : []);
            })
            .catch(e => { if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Failed to load'); });
        return () => { cancelled = true; };
    }, [customerId, groupKey]);

    const selectedGroups = useMemo(
        () => (data?.groups ?? []).filter(g => selected.includes(g.groupKey)),
        [data, selected]);

    // The subject follows the ticked combinations until the user edits it.
    useEffect(() => {
        if (!data || subjectDirty) return;
        setSubject(defaultSubject(data.customerName, selectedGroups.map(g => g.productName ?? '').filter(Boolean)));
    }, [data, selectedGroups, subjectDirty]);

    // Focus in, Tab kept inside, Esc closes (not while sending), focus back to the button.
    const panel = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    useDialog(panel, () => { if (!sending) onClose(); });

    const sender = (data?.senderEmail ?? '').toLowerCase();
    const picked = useMemo(() => new Set([...to, ...cc].map(e => e.email)), [to, cc]);

    const results = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!data || q.length < 2) return [];
        // The sender may pick themselves for To (the server then leaves them off Cc); only an
        // address already picked is hidden.
        return data.employees
            .filter(e => !picked.has(e.email))
            .filter(e => e.name.toLowerCase().includes(q)
                || e.email.includes(q)
                || (e.department?.toLowerCase().includes(q) ?? false)
                || (e.jobTitle?.toLowerCase().includes(q) ?? false))
            .slice(0, 12);
    }, [data, search, picked]);

    const add = useCallback((e: ForwardEmployee) => {
        (target === 'to' ? setTo : setCc)(prev => [...prev, e]);
        setSearch('');
    }, [target]);

    const remove = useCallback((which: Target, email: string) => {
        (which === 'to' ? setTo : setCc)(prev => prev.filter(e => e.email !== email));
    }, []);

    const toggleGroup = useCallback((key: string) => {
        setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    }, []);

    const subjectError = !subject.trim()
        ? 'A subject is required.'
        : subject.includes('<') ? 'The subject must be plain text.' : null;
    const recipientCount = to.length + cc.length;
    const senderInTo = to.some(e => e.email === sender);
    const canSend = !!data && !sending && to.length > 0 && recipientCount <= MAX_RECIPIENTS
        && selected.length > 0 && selected.length <= MAX_GROUPS
        && !subjectError && message.length <= MESSAGE_MAX;

    const handleSend = useCallback(async () => {
        if (!data || !canSend) return;
        setSending(true);
        try {
            const r = await consolidatedBusinessApi.sendForward({
                customerId: data.customerId,
                groupKeys: selected,
                subject: subject.trim(),
                message,
                to: to.map(e => ({ email: e.email, name: e.name })),
                cc: cc.map(e => ({ email: e.email, name: e.name })),
            });
            toast(`Forwarded to ${r.recipientCount} recipient(s)`, 'success');
            onSent();
            onClose();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to send', 'error');
        } finally {
            setSending(false);
        }
    }, [data, canSend, selected, subject, message, to, cc, toast, onSent, onClose]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="presentation">
            <div
                ref={panel}
                role="dialog"
                aria-modal="true"
                aria-labelledby="cb-forward-title"
                tabIndex={-1}
                className="flex max-h-[92vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl"
            >
                <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                    <h2 id="cb-forward-title" className="flex min-w-0 items-center gap-2 text-sm font-bold text-gray-900">
                        <Forward className="h-4 w-4 shrink-0 text-blue-600" />
                        <span className="truncate">Forward to colleagues{data ? ` — ${data.customerName}` : ''}</span>
                    </h2>
                    <button type="button" onClick={onClose} className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700" aria-label="Close">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
                    {loadError && <p role="alert" className="text-sm text-red-600">{loadError}</p>}
                    {!data && !loadError && (
                        <p className="flex items-center gap-2 text-sm text-gray-500">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                        </p>
                    )}

                    {data && (
                        <>
                            <p className="flex items-start gap-1.5 text-xs text-gray-500">
                                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                Internal only — recipients must be Dolder employees. Customers cannot be e-mailed from this page.
                            </p>

                            {/* Recipients */}
                            <div>
                                <div className="mb-1 flex items-center gap-2">
                                    <label htmlFor="cb-forward-search" className="text-xs font-medium text-gray-700">
                                        Add colleague
                                    </label>
                                    <div className="ml-auto inline-flex overflow-hidden rounded-md border border-gray-300 text-xs" role="group" aria-label="Add as">
                                        {(['to', 'cc'] as const).map(t => (
                                            <button
                                                key={t}
                                                type="button"
                                                onClick={() => setTarget(t)}
                                                aria-pressed={target === t}
                                                className={`px-2 py-0.5 ${target === t ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                                            >
                                                {t === 'to' ? 'To' : 'Cc'}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="relative">
                                    <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                                    <input
                                        ref={searchRef}
                                        id="cb-forward-search"
                                        type="text"
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' && results[0]) { e.preventDefault(); add(results[0]); }
                                        }}
                                        placeholder="Name, e-mail, department…"
                                        autoComplete="off"
                                        className="w-full rounded border border-gray-300 py-1.5 pl-7 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                {results.length > 0 && (
                                    <ul className="mt-1 max-h-48 overflow-y-auto rounded border border-gray-200">
                                        {results.map(e => (
                                            <li key={e.email}>
                                                <button
                                                    type="button"
                                                    onClick={() => add(e)}
                                                    className="w-full border-b border-gray-100 px-2 py-1.5 text-left text-sm last:border-b-0 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
                                                >
                                                    <span className="font-medium text-gray-900">{e.name}</span>
                                                    <span className="block text-xs text-gray-500">
                                                        {e.email}{e.jobTitle ? ` · ${e.jobTitle}` : ''}{e.department ? ` · ${e.department}` : ''}
                                                    </span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {search.trim().length >= 2 && results.length === 0 && (
                                    <p className="mt-1 text-xs text-gray-500">No active employee matches “{search.trim()}”.</p>
                                )}

                                <RecipientRow label="To" items={to} onRemove={email => remove('to', email)} />
                                <RecipientRow
                                    label="Cc"
                                    items={cc}
                                    self={senderInTo ? undefined : data.senderEmail}
                                    onRemove={email => remove('cc', email)}
                                />
                                {recipientCount > MAX_RECIPIENTS && (
                                    <p className="mt-1 text-xs text-red-600">At most {MAX_RECIPIENTS} recipients per mail.</p>
                                )}
                            </div>

                            {/* Combinations */}
                            <div>
                                <p className="mb-1 text-xs font-medium text-gray-700">
                                    Combinations ({selected.length} of {data.groups.length})
                                </p>
                                <div className="max-h-44 overflow-y-auto rounded border border-gray-200">
                                    {data.groups.map(g => {
                                        const meta = CB_STATUS_META[toCbStatus(g.status)];
                                        return (
                                            <label
                                                key={g.groupKey}
                                                className="flex cursor-pointer items-center gap-2 border-b border-gray-100 px-2 py-1.5 text-xs last:border-b-0 hover:bg-gray-50"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selected.includes(g.groupKey)}
                                                    onChange={() => toggleGroup(g.groupKey)}
                                                    className="h-3.5 w-3.5"
                                                />
                                                <span className="min-w-0 flex-1 truncate text-gray-900">{g.productName}</span>
                                                <span className="shrink-0 text-gray-500">{meta.label}</span>
                                                <span className="w-20 shrink-0 text-right tabular-nums text-gray-500">{fmtDate(g.lastInvoice)}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                                {selected.length > MAX_GROUPS && (
                                    <p className="mt-1 text-xs text-red-600">One mail can cover at most {MAX_GROUPS} combinations.</p>
                                )}
                            </div>

                            {/* Subject + message */}
                            <div>
                                <label htmlFor="cb-forward-subject" className="mb-1 block text-xs font-medium text-gray-700">Subject</label>
                                <input
                                    id="cb-forward-subject"
                                    type="text"
                                    value={subject}
                                    maxLength={SUBJECT_MAX}
                                    onChange={e => { setSubject(e.target.value); setSubjectDirty(true); }}
                                    className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {subjectError && subjectDirty && <p role="alert" className="mt-1 text-xs text-red-600">{subjectError}</p>}
                            </div>
                            <div>
                                <div className="mb-1 flex items-baseline gap-2">
                                    <label htmlFor="cb-forward-message" className="text-xs font-medium text-gray-700">Message</label>
                                    <span id="cb-forward-message-hint" className="text-xs text-gray-500">
                                        the customer and a table of the ticked combinations are added below it
                                    </span>
                                </div>
                                <textarea
                                    id="cb-forward-message"
                                    aria-describedby="cb-forward-message-hint"
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    rows={6}
                                    maxLength={MESSAGE_MAX}
                                    placeholder="What should your colleague look at?"
                                    className="w-full resize-y rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </>
                    )}
                </div>

                <div className="flex items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="min-w-0 truncate text-xs text-gray-500">
                        {data && (
                            <>
                                Sending as <span className="font-medium text-gray-700">{data.senderEmail}</span>
                                {!senderInTo && <> · you are always on Cc</>}
                            </>
                        )}
                    </p>
                    <div className="flex shrink-0 gap-2">
                        <button
                            onClick={onClose}
                            disabled={sending}
                            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSend}
                            disabled={!canSend}
                            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            {sending ? 'Sending…' : 'Send'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function RecipientRow({ label, items, self, onRemove }: {
    label: string;
    items: ForwardEmployee[];
    self?: string;
    onRemove: (email: string) => void;
}) {
    return (
        <div className="mt-2 flex items-start gap-2">
            <span className="w-6 shrink-0 pt-1 text-xs font-medium text-gray-500">{label}</span>
            <div className="flex min-h-[30px] flex-1 flex-wrap gap-1.5 rounded border border-gray-200 bg-white px-2 py-1">
                {self && (
                    <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700" title="Always on Cc">
                        {self} <span className="ml-1 text-gray-500">(you)</span>
                    </span>
                )}
                {items.map(e => (
                    <span key={e.email} className="inline-flex items-center gap-1 rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800" title={e.email}>
                        {e.name}
                        <button type="button" onClick={() => onRemove(e.email)} className="rounded hover:bg-blue-200" aria-label={`Remove ${e.email}`}>
                            <X className="h-3 w-3" />
                        </button>
                    </span>
                ))}
                {items.length === 0 && !self && <span className="py-0.5 text-xs text-gray-500">Nobody yet</span>}
            </div>
        </div>
    );
}
